import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Typography } from '@ag1/ui';
import { vars } from '@ag1/ui/theme';
import { type ImageContentHighlight } from 'types/studio/studio';
import { CustomPortableText } from 'shared/studio/PortableText';
import { StudioHotspotImageV2 } from 'shared/studio/StudioHotspotImage/StudioHotspotImage';
import * as styles from './ImageContentHighlightCard.css';

type ImageContentHighlightCardProps = Pick<ImageContentHighlight, 'cards'>;

const ImageContentHighlightCard = ({ cards }: ImageContentHighlightCardProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const dotsRef = useRef<HTMLDivElement>(null);

  const hasCards = cards && cards.length > 0;

  useEffect(() => {
    if (!hasCards) return;

    // Show the card and animate the progress bar for about 3 seconds
    const animationInterval = setInterval(() => {
      setIsVisible(false); // start fade-out

      setTimeout(() => {
        setPreviousIndex(currentIndex); // trigger exit animation on the dot
        setCurrentIndex((prev) => (prev + 1) % cards.length); // switch to next card

        setTimeout(() => {
          setIsVisible(true); // start fade-in

          setTimeout(() => {
            setPreviousIndex(null); // clear previous dot state
          }, 800); // duration of the dot exit animation
        }, 50); // small delay before showing new card
      }, 500); // duration of the fade-out
    }, 2750);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(animationInterval);
  }, [cards, currentIndex, hasCards]);

  if (!hasCards) return null;

  const {
    _key: key,
    cardSuperHeadline,
    visuals,
    cardNumber,
    cardIcon,
    cardText
  } = cards[currentIndex];

  const shouldShowNumber = visuals === 'number';
  const shouldShowIcon = visuals === 'icon';

  return (
    <div className={styles.root}>
      <div key={key} className={clsx(styles.card, isVisible && styles.fadeIn)}>
        <Typography variant="captionMono" size="2" noPadding className={styles.cardSuperHeadline}>
          <CustomPortableText addAccessibility value={cardSuperHeadline} />
        </Typography>

        {shouldShowNumber && <p className={styles.cardNumber}>{cardNumber}</p>}
        <div className={styles.textAndDotsWrapper}>
          <div className={clsx(shouldShowIcon && styles.iconWrapper)}>
            {shouldShowIcon && cardIcon && (
              <StudioHotspotImageV2 image={cardIcon} className={styles.cardIcon} />
            )}
            <Typography component="p" variant="body" size="2" noPadding>
              {cardText}
            </Typography>
          </div>

          <div
            className={styles.dotsPlaceholder}
            style={{
              width: `${dotsRef.current?.offsetWidth || 100}px`,
              flex: `0 0 ${dotsRef.current?.offsetWidth || 100}px`
            }}
          />
        </div>
      </div>

      <div
        className={styles.dots}
        style={
          {
            '--dot-accent-color': shouldShowNumber
              ? vars.colors.successBanner.text
              : vars.colors.flavors['agz-original']
          } as React.CSSProperties
        }
        ref={dotsRef}>
        {cards.map(({ _key: cardKey }, i) => {
          const isActive = i === currentIndex;
          const isFadingOut = i === previousIndex;
          return (
            <div
              key={cardKey}
              className={clsx(
                styles.dot,
                isActive && styles.activeDot,
                isFadingOut && styles.fadingDot
              )}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ImageContentHighlightCard;
